// Type definitions for the admin portal
export interface Disease {
  id: string;
  name: string;
  icd10_code?: string;
  short_description?: string;
  content?: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  thumbnail_url?: string;
  images?: DiseaseImage[];
  treatments?: Treatment[];
}

export interface DiseaseImage {
  id: string;
  disease_id: string;
  image_url: string;
  thumbnail_url?: string;
  body_location?: string;
  severity?: 'MILD' | 'MODERATE' | 'SEVERE';
  fitzpatrick_skin_tone?: number;
  is_primary: boolean;
}

export interface Treatment {
  id: string;
  name: string;
  description?: string;
  treatment_type: 'PRESCRIPTION' | 'OTC' | 'HOME_REMEDY' | 'LIFESTYLE' | 'PROCEDURE' | 'OTHER';
  instructions?: string;
  regular_price?: number;
  sale_price?: number;
  image_url?: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface DiseaseInput {
  name: string;
  icd10_code?: string;
  short_description?: string;
  content?: string;
  is_active?: boolean;
}

export interface UpdateDiseaseInput {
  name?: string;
  icd10_code?: string;
  short_description?: string;
  content?: string;
  is_active?: boolean;
}

export interface DiseaseImageInput {
  disease_id: string;
  image_url: string;
  thumbnail_url?: string;
  body_location?: string;
  severity?: 'MILD' | 'MODERATE' | 'SEVERE';
  fitzpatrick_skin_tone?: number;
  is_primary?: boolean;
}

export interface TreatmentInput {
  name: string;
  description?: string;
  treatment_type: 'PRESCRIPTION' | 'OTC' | 'HOME_REMEDY' | 'LIFESTYLE' | 'PROCEDURE' | 'OTHER';
  instructions?: string;
  regular_price?: number;
  sale_price?: number;
  image_url?: string;
  is_active?: boolean;
}

export interface UpdateTreatmentInput {
  name?: string;
  description?: string;
  treatment_type?: 'PRESCRIPTION' | 'OTC' | 'HOME_REMEDY' | 'LIFESTYLE' | 'PROCEDURE' | 'OTHER';
  instructions?: string;
  regular_price?: number;
  sale_price?: number;
  image_url?: string;
  is_active?: boolean;
}

export interface TrainingSample {
  id: string;
  original_image_url: string;
  thumbnail_url?: string;
  symptoms_description?: string;
  prediction?: string;
  confidence?: number;
  differential_diagnoses?: any;
  model_version?: string;
  ai_summary?: string;
  review_status: ReviewStatus;
  reviewed_by?: string;
  reviewed_at?: string;
  correct_diagnosis?: string;
  staff_notes?: string;
  is_validated: boolean;
  validation_quality?: ValidationQuality;
  use_for_training: boolean;
  training_category?: string;
  image_quality?: ImageQuality;
  diagnosis_clarity?: DiagnosisClarity;
  label_id?: string;
  disease?: Disease;
  body_zone_ids?: BodyZoneId[];
  llm_analysis?: LLMAnalysis;
  created_at: string;
  updated_at: string;
}

export interface LLMAnalysis {
  diagnosis?: string;
  confidence?: number;
  why?: string;
  questions?: string;
}

export enum ReviewStatus {
  PENDING = 'PENDING',
  IN_REVIEW = 'IN_REVIEW',
  REVIEWED = 'REVIEWED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED'
}

export enum ValidationQuality {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  FAIR = 'FAIR',
  POOR = 'POOR'
}

export enum ImageQuality {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  FAIR = 'FAIR',
  POOR = 'POOR'
}

export enum DiagnosisClarity {
  CLEAR = 'CLEAR',
  SOMEWHAT_CLEAR = 'SOMEWHAT_CLEAR',
  UNCLEAR = 'UNCLEAR'
}

export interface CreateTrainingSampleInput {
  original_image_url: string;
  thumbnail_url?: string;
  symptoms_description?: string;
  prediction?: string;
  confidence?: number;
  differential_diagnoses?: any;
  model_version?: string;
  ai_summary?: string;
  training_category?: string;
  image_quality?: ImageQuality;
  diagnosis_clarity?: DiagnosisClarity;
  body_zone_ids?: BodyZoneId[];
}

export interface UpdateTrainingSampleInput {
  symptoms_description?: string;
  prediction?: string;
  confidence?: number;
  differential_diagnoses?: any;
  model_version?: string;
  ai_summary?: string;
  review_status?: ReviewStatus;
  correct_diagnosis?: string;
  staff_notes?: string;
  is_validated?: boolean;
  validation_quality?: ValidationQuality;
  use_for_training?: boolean;
  training_category?: string;
  image_quality?: ImageQuality;
  diagnosis_clarity?: DiagnosisClarity;
  label_id?: string;
  body_zone_ids?: BodyZoneId[];
}

export interface TrainingSampleFilterInput {
  review_status?: ReviewStatus;
  is_validated?: boolean;
  use_for_training?: boolean;
  validation_quality?: ValidationQuality;
  training_category?: string;
  model_version?: string;
  label_id?: string;
}

export interface DiagnosisCount {
  disease_id: string;
  disease_name: string;
  count: number;
  percentage: number;
}

export interface TopDiagnosesResponse {
  items: DiagnosisCount[];
  total_diagnoses: number;
  time_period_hours: number;
  generated_at: string;
}

// Anatomy Selector Types
export enum BodyZoneId {
  face = 'face',
  posteriorHead = 'posteriorHead',
  anteriorNeck = 'anteriorNeck',
  posteriorNeck = 'posteriorNeck',
  anteriorTrunk = 'anteriorTrunk',
  posteriorTrunk = 'posteriorTrunk',
  anteriorLeftArm = 'anteriorLeftArm',
  posteriorLeftArm = 'posteriorLeftArm',
  anteriorRightArm = 'anteriorRightArm',
  posteriorRightArm = 'posteriorRightArm',
  anteriorLeftHand = 'anteriorLeftHand',
  posteriorLeftHand = 'posteriorLeftHand',
  anteriorRightHand = 'anteriorRightHand',
  posteriorRightHand = 'posteriorRightHand',
  pubicRegion = 'pubicRegion',
  anteriorLeftThigh = 'anteriorLeftThigh',
  posteriorLeftThigh = 'posteriorLeftThigh',
  anteriorRightThigh = 'anteriorRightThigh',
  posteriorRightThigh = 'posteriorRightThigh',
  anteriorLeftCalf = 'anteriorLeftCalf',
  posteriorLeftCalf = 'posteriorLeftCalf',
  anteriorRightCalf = 'anteriorRightCalf',
  posteriorRightCalf = 'posteriorRightCalf',
  leftFoot = 'leftFoot',
  rightFoot = 'rightFoot',
  leftButtocks = 'leftButtocks',
  rightButtocks = 'rightButtocks',
}

export enum ViewType {
  front = 'front',
  back = 'back',
}

export interface BodyZone {
  id: BodyZoneId;
  svgAssetPath: string;
  top: number;
  left: number;
  height: number;
  isSelected?: boolean;
}
